import { defineStore } from "pinia";
import { computed, ComputedRef, ref, watch } from "vue";
import { useSessionStorage } from "@vueuse/core";
import { TKid } from "stores/useKidsStore";
import {
  CntJournalListResponse,
  DashboardResponseSchedule,
  EvaluationResponse,
  JournalListResponse,
  JournalResponse,
  OrganizationResponse,
  SchoolReadinessResponse,
} from "src/generated/formeapi";
import useFormeServer from "src/composables/server/useFormeServer";
import { isString } from "src/types/typeGuard";
import useTime from "src/composables/useTime";
import { date } from "quasar";

export type KidInfoFormType = {
  firstName: string;
  lastName: string;
  birth: string;
  gender: string;
  phone: string;
  grade?: number;
  class?: string;
  institutionType?: number;
  organizationName?: string;
  region?: string;
  organization?: OrganizationResponse;
  firstGroup?: string;
  model?: string;
};

export type AddKidType = KidInfoFormType & {
  entryUUID: string;
  licenseCode: string;
  exID: string;
};

export type AppendixType = {
  birth: string;
  class: number;
  grade: number;
  gender: string;
  lastName: string;
  firstName: string;
  schoolName: string;
  fullName: string;
  contactNumber?: string;
};

type WeekType = {
  weekNum: number;
  weekRange: Date[];
};

type CustomStatusReportListResponse = {
  evaluation: (WeekType & EvaluationResponse)[];
  schoolReadiness: (WeekType & SchoolReadinessResponse)[];
};

export const useKidStore = defineStore("kid", () => {
  const kid = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/kid`,
    {} as TKid
  );

  const getKid: ComputedRef<TKid> = computed(() => {
    return { ...kid.value };
  });

  function setKid(kidData: TKid) {
    kid.value = kidData;
  }

  function resetKid() {
    kid.value = {} as TKid;
  }

  // 아이 등록시 사용
  const newKid = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/newKid`,
    {
      firstName: "",
      lastName: "",
      birth: "",
      gender: "M",
      grade: 0,
      phone: "",
      licenseCode: "",
      exID: "",
      entryUUID: "",
    } as AddKidType
  );

  const getNewKid: ComputedRef<AddKidType> = computed(() => {
    return { ...newKid.value };
  });

  function setNewKid(kidData: AddKidType) {
    newKid.value = kidData;
  }

  function resetNewKid() {
    newKid.value = {
      firstName: "",
      lastName: "",
      birth: "",
      gender: "M",
      phone: "",
      licenseCode: "",
      exID: "",
    } as AddKidType;
  }

  // 아이의 기분
  const kidEmotions = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/kidEmotions`,
    [] as JournalResponse[]
  );

  const getKidEmotions: ComputedRef<JournalResponse[]> = computed(() => {
    return [...kidEmotions.value];
  });

  function setKidEmotions(journalListResponse: JournalListResponse) {
    kidEmotions.value = journalListResponse.groups?.reduce(
      (previousValue, currentValue) =>
        previousValue.concat(currentValue.journals),
      [] as JournalResponse[]
    );
  }

  function resetKidEmotions() {
    kidEmotions.value = [] as JournalResponse[];
  }

  // 아이 task
  const kidSchedules = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/kidSchedules`,
    [] as DashboardResponseSchedule[]
  );

  const getKidSchedules: ComputedRef<DashboardResponseSchedule[]> = computed(
    () => {
      return [...kidSchedules.value];
    }
  );

  function setKidSchedules(kidSchedulesData: DashboardResponseSchedule[]) {
    kidSchedules.value = kidSchedulesData;
  }

  function resetKidSchedules() {
    kidSchedules.value = {} as DashboardResponseSchedule[];
  }

  // 주차 날짜 계산
  const weeks = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/weeks`,
    [] as WeekType[]
  );

  const getWeeks: ComputedRef<WeekType[]> = computed(() => {
    return [...weeks.value];
  });

  function setWeeks(): void {
    const { getWeekDateRange } = useTime();
    const usePeriod = kid.value.usePeriod;
    const planStartDT = kid.value.planStartDT ?? "";

    // 오늘이 몇주차 인지 계산
    const weekNum = Math.floor(usePeriod / 7) + 1;

    const res: Date[][] = [];
    for (let i = weekNum; i > 0; i--) {
      res.push(getWeekDateRange(i, planStartDT));
    }

    weeks.value = res
      .reverse()
      .map((week, index) => ({ weekNum: index + 1, weekRange: week }));
  }

  // 아이 Report
  const kidReports = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/kidReports`,
    {} as CustomStatusReportListResponse
  );

  const getKidReports: ComputedRef<CustomStatusReportListResponse> = computed(
    () => {
      return { ...kidReports.value };
    }
  );

  function resetKidReports() {
    kidReports.value = {} as CustomStatusReportListResponse;
  }

  // 서버데이터 호출
  const { v2ResourcesListUserStatusReport } = useFormeServer();
  const loading = ref(false);

  async function setKidReports() {
    try {
      loading.value = true;
      const data = await v2ResourcesListUserStatusReport(kid.value.UUID);

      if (isString(data)) {
        resetKidReports();
        console.error("useKidStore - setKidReports ::", data);

        return;
      }

      data.evaluation?.sort((a, b) => {
        if (a.date < b.date) return -1;
        return 1;
      });

      data.schoolReadiness?.sort((a, b) => {
        if (a.date < b.date) return -1;
        return 1;
      });

      kidReports.value = {
        schoolReadiness: data.schoolReadiness?.map((schoolReadiness) => {
          const index = getWeeks.value.findIndex((week) =>
            date.isBetweenDates(
              schoolReadiness.date,
              week.weekRange[0],
              week.weekRange[1],
              {
                inclusiveFrom: true,
                inclusiveTo: true,
                onlyDate: false,
              }
            )
          );
          if (index < 0) return {} as WeekType & SchoolReadinessResponse;

          return {
            weekNum: index + 1,
            weekRange: getWeeks.value[index].weekRange,
            ...schoolReadiness,
          };
        }),

        evaluation: data.evaluation?.map((evaluation) => {
          const index = getWeeks.value.findIndex((week) =>
            date.isBetweenDates(
              evaluation.date,
              week.weekRange[0],
              week.weekRange[1],
              {
                inclusiveFrom: true,
                inclusiveTo: true,
                onlyDate: false,
              }
            )
          );

          if (index < 0) return {} as WeekType & EvaluationResponse;

          return {
            weekNum: index + 1,
            weekRange: getWeeks.value[index].weekRange,
            ...evaluation,
          };
        }),
      };
    } catch (e) {
      console.error("useKidStore - setKidReports ::", e);
    } finally {
      loading.value = false;
    }
  }

  // 뽀미 도와주기 결과
  const helpingResult = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/helpingResult`,
    {} as CntJournalListResponse
  );

  const getHelpingResult: ComputedRef<CntJournalListResponse> = computed(() => {
    return { ...helpingResult.value };
  });

  function resetHelpingResult() {
    helpingResult.value = {} as CntJournalListResponse;
  }

  function setHelpingResult(newResult: CntJournalListResponse) {
    resetHelpingResult();
    helpingResult.value = { ...newResult };
  }

  watch(
    () => kid.value,
    async () => {
      setWeeks();
      await setKidReports();
    }
  );

  return {
    getKid,
    setKid,
    resetKid,
    getNewKid,
    setNewKid,
    resetNewKid,
    getKidEmotions,
    setKidEmotions,
    resetKidEmotions,
    getKidSchedules,
    setKidSchedules,
    resetKidSchedules,
    getHelpingResult,
    resetHelpingResult,
    setHelpingResult,
    getWeeks,
    setWeeks,
    getKidReports,
    setKidReports,
    loading,
  };
});
